import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  carryForwardBullet,
  carryForwardExpiryBullet,
  carryForwardHeadline,
  carryForwardTaxBullet,
  coContributionBullet,
  coContributionConditionsBullet,
  coContributionHeadline,
  formatAud,
  sacrificeBullet,
  sacrificeHeadline,
  sacrificeListoBullet,
  sourceBullet,
  spouseOffsetBullet,
  spouseOffsetHeadline,
} from "@aucash/rules/super-boost";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented } from "../../src/components/controls";
import { FULL_DISCLAIMER, SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// super-boost — the "super money you're missing + catch-up" calculator,
// surfaced stateless-v1 (PLAN arch decision #1). One screen, four modes over
// the shared super ruleset; the result renders INLINE (no save-by-id, no auth
// gate, no persistence). Each compute is a stateless `calc.superBoost.compute`
// query — input -> output, no round-trip to a stored row.
//
// Copy comes verbatim from the rules layer's copy helpers so the wording can
// never drift from the API/SEO surfaces (validation §3: information, not
// advice). The co-contribution / spouse offset / carry-forward headroom are
// presented as cited ENTITLEMENT ARITHMETIC — never "you should contribute" /
// "put money in super". The cap-expiry date is a CALM dated fact, never an
// urgency nudge.
//
// HARD RULE (panel C2): superannuation is an s764A financial product — there is
// NO fund / super-product handoff anywhere on this surface ($0 affiliate by
// design). The rules-layer `assertNoFundHandoff` guard strips any such term
// from the output we render, and the API re-asserts it over the whole response.

const ASSESSMENT_YEAR = "2025-26";

type Mode =
  | "co-contribution"
  | "spouse-offset"
  | "carry-forward"
  | "salary-sacrifice";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "co-contribution", label: "Co-contribution" },
  { value: "spouse-offset", label: "Spouse offset" },
  { value: "carry-forward", label: "Carry-forward" },
  { value: "salary-sacrifice", label: "Salary sacrifice" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  "co-contribution": {
    eyebrow: "FY26 · government co-contribution",
    title: "Could the government add to your super?",
    blurb:
      "For lower incomes, the ATO can add up to A$500 to your super — 50 cents for every A$1 of eligible after-tax contribution. See the entitlement amount for your income, with the eligibility conditions listed.",
  },
  "spouse-offset": {
    eyebrow: "FY26 · spouse contributions",
    title: "Spouse-contribution tax offset",
    blurb:
      "Contributing to a lower-income spouse's super can attract a tax offset of up to A$540. See the offset that the ATO rules produce for your spouse's income and the contribution you describe.",
  },
  "carry-forward": {
    eyebrow: "FY26 · concessional cap",
    title: "Unused concessional cap you can carry forward",
    blurb:
      "Unused concessional (before-tax) cap from the past five years can be carried forward. See your total available headroom and a calm, dated note of when the oldest amount expires.",
  },
  "salary-sacrifice": {
    eyebrow: "FY26 · concessional tax",
    title: "What's the concession on a before-tax contribution?",
    blurb:
      "Concessional contributions are taxed at 15% in your fund instead of your marginal income-tax rate. See the difference per dollar at your income — stated as the rule, not a recommendation.",
  },
};

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.superBoost.compute.query>
>;

export default function SuperBoostIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("co-contribution");

  // co-contribution
  const [totalIncome, setTotalIncome] = useState("");
  const [personalContribution, setPersonalContribution] = useState("");
  const [ageAtYearEnd, setAgeAtYearEnd] = useState("");

  // spouse-offset
  const [spouseIncome, setSpouseIncome] = useState("");
  const [spouseContribution, setSpouseContribution] = useState("");

  // carry-forward — a simple two-year history capture (the most recent FYs),
  // each as a "cap" and "contributions made" pair.
  const [priorYearTsb, setPriorYearTsb] = useState("");
  const [capRecent, setCapRecent] = useState("30000");
  const [contribRecent, setContribRecent] = useState("");
  const [capPrev, setCapPrev] = useState("27500");
  const [contribPrev, setContribPrev] = useState("");

  // salary-sacrifice
  const [sacrificeAmount, setSacrificeAmount] = useState("");

  // shared — taxable income drives the (API-derived) marginal rate for the
  // carry-forward + salary-sacrifice modes. The user supplies an income, never
  // a raw rate; the server resolves the with-Medicare rate from income-tax.
  const [taxableIncome, setTaxableIncome] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.superBoost.compute.query>[0]["input"];
  } | null => {
    switch (mode) {
      case "co-contribution":
        return {
          input: {
            mode: "co-contribution",
            totalIncome: num(totalIncome),
            personalContribution: num(personalContribution),
            eligibility: { ageAtYearEnd: num(ageAtYearEnd) },
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
      case "spouse-offset":
        return {
          input: {
            mode: "spouse-offset",
            spouseIncome: num(spouseIncome),
            contribution: num(spouseContribution),
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
      case "carry-forward":
        return {
          input: {
            mode: "carry-forward",
            history: [
              { fy: "2024-25", cap: num(capRecent), contributions: num(contribRecent) },
              { fy: "2023-24", cap: num(capPrev), contributions: num(contribPrev) },
            ],
            priorYearTsb: num(priorYearTsb),
            taxableIncome: num(taxableIncome),
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
      case "salary-sacrifice":
        return {
          input: {
            mode: "salary-sacrifice",
            contribution: num(sacrificeAmount),
            taxableIncome: num(taxableIncome),
            adjustedTaxableIncome: num(taxableIncome),
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
    }
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    switch (mode) {
      case "co-contribution":
        if (totalIncome.trim() === "")
          next.income = "Enter your total income for the year.";
        if (personalContribution.trim() === "")
          next.contribution = "Enter the after-tax contribution you made (or plan to).";
        if (ageAtYearEnd.trim() === "")
          next.age = "Enter your age at the end of the financial year.";
        break;
      case "spouse-offset":
        if (spouseIncome.trim() === "")
          next.spouseIncome = "Enter your spouse's income for the year.";
        if (spouseContribution.trim() === "")
          next.spouseContribution = "Enter the contribution you made for your spouse.";
        break;
      case "carry-forward":
        if (priorYearTsb.trim() === "")
          next.tsb = "Enter your total super balance at the previous 30 June.";
        if (taxableIncome.trim() === "")
          next.taxable = "Enter your taxable income for the year.";
        break;
      case "salary-sacrifice":
        if (sacrificeAmount.trim() === "")
          next.sacrifice = "Enter the before-tax contribution you want to model.";
        if (taxableIncome.trim() === "")
          next.taxable = "Enter your taxable income for the year.";
        break;
    }
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.superBoost.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Super boost" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which entitlement are you checking?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "co-contribution" ? (
            <>
              <Field
                label="Your total income for FY26"
                help="Assessable income plus reportable fringe benefits and reportable employer super, less allowable business deductions."
                error={errors.income}
              >
                <MoneyInput
                  value={totalIncome}
                  onChange={setTotalIncome}
                  testID="input-total-income"
                  accessibilityLabel="Your total income for FY26"
                />
              </Field>
              <Field
                label="After-tax (non-concessional) contribution"
                help="The eligible personal contribution you made from your take-home pay."
                error={errors.contribution}
              >
                <MoneyInput
                  value={personalContribution}
                  onChange={setPersonalContribution}
                  testID="input-personal-contribution"
                  accessibilityLabel="After-tax contribution"
                />
              </Field>
              <Field
                label="Your age at the end of the financial year"
                help="The co-contribution requires you to be under 71 at year end."
                error={errors.age}
              >
                <MoneyInput
                  value={ageAtYearEnd}
                  onChange={setAgeAtYearEnd}
                  testID="input-age"
                  accessibilityLabel="Your age at the end of the financial year"
                />
              </Field>
            </>
          ) : null}

          {mode === "spouse-offset" ? (
            <>
              <Field
                label="Your spouse's income for FY26"
                help="Their assessable income plus reportable fringe benefits and reportable super."
                error={errors.spouseIncome}
              >
                <MoneyInput
                  value={spouseIncome}
                  onChange={setSpouseIncome}
                  testID="input-spouse-income"
                  accessibilityLabel="Your spouse's income for FY26"
                />
              </Field>
              <Field
                label="Contribution you made for your spouse"
                error={errors.spouseContribution}
              >
                <MoneyInput
                  value={spouseContribution}
                  onChange={setSpouseContribution}
                  testID="input-spouse-contribution"
                  accessibilityLabel="Contribution you made for your spouse"
                />
              </Field>
            </>
          ) : null}

          {mode === "carry-forward" ? (
            <>
              <Field
                label="Total super balance at the previous 30 June"
                help="Carry-forward amounts can only be used in a year when this was below A$500,000."
                error={errors.tsb}
              >
                <MoneyInput
                  value={priorYearTsb}
                  onChange={setPriorYearTsb}
                  testID="input-prior-tsb"
                  accessibilityLabel="Total super balance at the previous 30 June"
                />
              </Field>
              <Field
                label="FY2024-25 — concessional cap"
                help="The general concessional cap that applied that year (A$30,000)."
              >
                <MoneyInput value={capRecent} onChange={setCapRecent} accessibilityLabel="FY2024-25 cap" />
              </Field>
              <Field label="FY2024-25 — concessional contributions you made">
                <MoneyInput
                  value={contribRecent}
                  onChange={setContribRecent}
                  testID="input-contrib-recent"
                  accessibilityLabel="FY2024-25 concessional contributions"
                />
              </Field>
              <Field
                label="FY2023-24 — concessional cap"
                help="The general concessional cap that applied that year (A$27,500)."
              >
                <MoneyInput value={capPrev} onChange={setCapPrev} accessibilityLabel="FY2023-24 cap" />
              </Field>
              <Field label="FY2023-24 — concessional contributions you made">
                <MoneyInput
                  value={contribPrev}
                  onChange={setContribPrev}
                  testID="input-contrib-prev"
                  accessibilityLabel="FY2023-24 concessional contributions"
                />
              </Field>
              <Field
                label="Your taxable income for FY26"
                help="Used to work out the income-tax rate the 15% concession is compared against."
                error={errors.taxable}
              >
                <MoneyInput
                  value={taxableIncome}
                  onChange={setTaxableIncome}
                  testID="input-taxable-income"
                  accessibilityLabel="Your taxable income for FY26"
                />
              </Field>
            </>
          ) : null}

          {mode === "salary-sacrifice" ? (
            <>
              <Field
                label="Before-tax contribution to model"
                help="The salary-sacrifice or personal-deductible amount."
                error={errors.sacrifice}
              >
                <MoneyInput
                  value={sacrificeAmount}
                  onChange={setSacrificeAmount}
                  testID="input-sacrifice-amount"
                  accessibilityLabel="Before-tax contribution to model"
                />
              </Field>
              <Field
                label="Your taxable income for FY26"
                help="Used to work out the income-tax rate the 15% in-fund rate is compared against."
                error={errors.taxable}
              >
                <MoneyInput
                  value={taxableIncome}
                  onChange={setTaxableIncome}
                  testID="input-taxable-income"
                  accessibilityLabel="Your taxable income for FY26"
                />
              </Field>
            </>
          ) : null}

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="super-boost-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer DISCLAIMER) sits at the TOP, before any
 * number (validation §3.2 placement rule). */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { headline, bullets } = renderFor(result);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{FULL_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · FY26</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        {bullets.map((b, i) => (
          <Text key={i} style={styles.bodyText}>
            {b}
          </Text>
        ))}
      </Card>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(result.fy, result.sourcesVerifiedAt)}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Pick the rules-layer copy helpers for the result's mode. All wording comes
 * from the rules package — no inline figures, no advice framing, no fund
 * handoff. */
function renderFor(result: ComputeResult): {
  headline: string;
  bullets: string[];
} {
  const out = result.output;
  switch (result.mode) {
    case "co-contribution": {
      const o = out as Extract<typeof out, { coContribution: number }>;
      return {
        headline: coContributionHeadline(o),
        bullets: [
          coContributionBullet(o),
          coContributionConditionsBullet(o),
        ].filter((b): b is string => b !== null),
      };
    }
    case "spouse-offset": {
      const o = out as Extract<typeof out, { offset: number }>;
      return {
        headline: spouseOffsetHeadline(o),
        bullets: [spouseOffsetBullet(o)],
      };
    }
    case "carry-forward": {
      const o = out as Extract<typeof out, { availableHeadroom: number }>;
      return {
        headline: carryForwardHeadline(o),
        bullets: [
          carryForwardBullet(o),
          carryForwardExpiryBullet(o),
          carryForwardTaxBullet(o),
        ].filter((b): b is string => b !== null),
      };
    }
    case "salary-sacrifice": {
      const o = out as Extract<typeof out, { taxSaving: number }>;
      return {
        headline: sacrificeHeadline(o),
        bullets: [sacrificeBullet(o), sacrificeListoBullet(o)].filter(
          (b): b is string => b !== null,
        ),
      };
    }
  }
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud` is imported for parity with the other screens' result helpers; the
// headline strings already embed the formatted figures, so it isn't called
// directly here. Referenced to keep the import meaningful without a no-op.
void formatAud;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
